import { OptionType } from '~/features/types';
import { AOISToOptions } from '~/features/vault/aoi/utils';
import { ListingSubspecialty } from '~/types/hlx/listingSubspecialty';
import { getData } from '~/utils/service';

export const GENERAL_WORK_REQUIRED_LABEL = 'General work required';

type ListingWithSubspecialties = {
  subspecialties?: ListingSubspecialty[] | null;
  generalSpecialtyFlag?: boolean | null;
};

/** The areas of interest that sit under one specialty, as select options. */
export const loadSubspecialtyOptions = async (specialtyId: string): Promise<OptionType[]> => {
  const options = await getData<{ label: string; value: string }[]>('aoisOptions', {
    pathParams: { specialtiesId: specialtyId },
    transformResponse: AOISToOptions,
  });
  return options.map(({ label, value }) => ({ label, value }));
};

export const subspecialtiesToOptions = (
  subspecialties: ListingSubspecialty[] | null | undefined
): OptionType[] =>
  (subspecialties ?? []).map((sub) => ({
    label: sub.aoiName,
    value: sub.areaOfInterestId.toString(),
  }));

/**
 * The request fields for the picked subspecialties. The array is always sent
 * (`[]` clears them on the server), and "General work required" is only
 * meaningful beside at least one subspecialty, so it is true otherwise.
 */
export const subspecialtyFieldsToApi = (
  subspecialties: readonly OptionType[],
  generalSpecialtyFlag: boolean
) => ({
  subspecialtyIds: subspecialties.map((sub) => Number(sub.value)),
  generalSpecialtyFlag: subspecialties.length > 0 ? generalSpecialtyFlag : true,
});

/** "A, B · General work required: Yes" -- null when the listing has no subspecialties. */
export const formatSubspecialties = (listing: ListingWithSubspecialties): string | null => {
  const names = (listing.subspecialties ?? []).map((sub) => sub.aoiName);
  if (names.length === 0) return null;
  const list = names.join(', ');
  if (listing.generalSpecialtyFlag == null) return list;
  return `${list} · ${GENERAL_WORK_REQUIRED_LABEL}: ${listing.generalSpecialtyFlag ? 'Yes' : 'No'}`;
};
